import * as Accordion from "@radix-ui/react-accordion"
import clsx from "clsx"
import { forwardRef } from "react"

import { faIcon } from "@utils/faIcon"

import { TruncatableText } from "@particles/index"

import { Icon } from "@atoms/index"

import { AlertBanner } from "@molecules/index"

import AccordionContent from "./AccordionContent/AccordionContent"
import styles from "./DisplayModule.module.scss"
import { DisplayModuleProps } from "./DisplayModule.types"

function DisplayModule(props: DisplayModuleProps, ref: React.Ref<HTMLDivElement>) {
    const {
        label,
        mode = "default",
        defaultOpen = false,
        isDisabled = false,
        avatar,
        caption,
        titleRight,
        headerAction1,
        headerAction2,
        showAlert,
        alertBanner,
        alertBannerStatus,
        className,
        children,
        contentClassName,
        isOpen,
        onOpenChange,
        ...rest
    } = props

    const baseClassName = clsx(styles.base, className, alertBannerStatus && styles[`${alertBannerStatus}Alert`])

    const isControlled = isOpen !== undefined && onOpenChange !== undefined

    const accordionValue = isOpen ? "module" : ""
    const onAccordionValueChange = (value: string) => {
        if (onOpenChange) {
            onOpenChange(value === "module")
        }
    }

    const alertBannerNode = showAlert ? (
        <AlertBanner type="banner" status={alertBannerStatus} {...alertBanner} />
    ) : null

    function renderHeaderTitle() {
        return (
            <>
                {avatar && <div className={styles.headerAvatar}>{avatar}</div>}
                <div className={styles.labelAndMeta}>
                    <div className={styles.titleRow}>
                        <TruncatableText
                            text={label}
                            tooltipOnTruncate={true}
                            tooltipPlacement="top"
                            className={styles.title}
                        />
                        {titleRight && <span className={styles.titleRight}>{titleRight}</span>}
                    </div>
                    {caption && <span className={styles.caption}>{caption}</span>}
                </div>
            </>
        )
    }

    function renderHeaderActions() {
        return (
            <div className={styles.actions}>
                {headerAction1}
                {headerAction2}
            </div>
        )
    }

    if (mode === "accordion") {
        return (
            <div ref={ref} className={baseClassName} {...rest}>
                {alertBannerNode}

                <Accordion.Root
                    type="single"
                    collapsible={true}
                    value={isControlled ? accordionValue : undefined}
                    onValueChange={isControlled ? onAccordionValueChange : undefined}
                    defaultValue={defaultOpen && !isControlled ? "module" : undefined}
                >
                    <Accordion.Item value="module" className={styles.item} disabled={isDisabled}>
                        {/* Actions sit beside the trigger, not inside it, so they stay valid interactive elements */}
                        <Accordion.Header asChild={true}>
                            <div className={styles.header}>
                                <Accordion.Trigger className={styles.trigger}>
                                    <Icon
                                        size={14}
                                        container="square"
                                        icon={faIcon({ name: "caret-down", style: "solid" })}
                                        className={styles.chevron}
                                    />
                                    {renderHeaderTitle()}
                                </Accordion.Trigger>
                                {renderHeaderActions()}
                            </div>
                        </Accordion.Header>
                        <AccordionContent className={clsx(styles.content, contentClassName)}>
                            {children}
                        </AccordionContent>
                    </Accordion.Item>
                </Accordion.Root>
            </div>
        )
    } else if (mode === "headless") {
        return (
            <div ref={ref} className={baseClassName} {...rest}>
                {alertBannerNode}
                <div className={clsx(styles.content, contentClassName)}>{children}</div>
            </div>
        )
    } else {
        return (
            <div ref={ref} className={baseClassName} {...rest}>
                {alertBannerNode}

                <div className={styles.header}>
                    {renderHeaderTitle()}
                    {renderHeaderActions()}
                </div>
                <div className={clsx(styles.content, contentClassName)}>{children}</div>
            </div>
        )
    }
}

export default forwardRef(DisplayModule)
